import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { TopicList } from "@/components/domain/topic-list"
import { TopicForm } from "@/components/domain/topic-form"
import { HelpHint } from "@/components/ui/help-hint"
import type { Topic, TopicAgent } from "@/types/topic"

type Props = {
  agents: TopicAgent[]
  selectedAgentId: number
  topics: Topic[]
  tagSuggestions: string[]
  topics_stream: string
}

const DRAFT_ID = -1

export default function TopicsIndex({ agents, selectedAgentId, topics, tagSuggestions, topics_stream }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(topics[0]?.id ?? null)
  const [isDraft, setIsDraft] = useState(false)
  const [deleteCandidate, setDeleteCandidate] = useState<Topic | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Live updates when other admins edit topics in this company.
  useInertiaCable(topics_stream, { only: ["topics"] })

  const selected = useMemo(() => {
    if (isDraft) return null
    return topics.find((t) => t.id === selectedId) ?? null
  }, [topics, selectedId, isDraft])

  // If the selected topic disappears (deleted by us or another admin),
  // fall back to the first one in the list.
  useEffect(() => {
    if (!isDraft && selectedId && !topics.find((t) => t.id === selectedId)) {
      setSelectedId(topics[0]?.id ?? null)
    }
  }, [topics, selectedId, isDraft])

  const handleAgentChange = (agentId: number) => {
    router.get("/topics", { agent_id: agentId }, { preserveScroll: true, preserveState: false })
  }

  const startNew = () => {
    setIsDraft(true)
    setSelectedId(DRAFT_ID)
  }

  const handleSelect = (id: number) => {
    setIsDraft(false)
    setSelectedId(id)
  }

  const handleSaved = () => {
    setIsDraft(false)
    if (selectedId === DRAFT_ID) setSelectedId(null) // useEffect picks topics[0]
  }

  const handleCancel = () => {
    setIsDraft(false)
    setSelectedId(topics[0]?.id ?? null)
  }

  const handleDelete = () => {
    if (!selected) return
    setDeleteCandidate(selected)
  }

  const confirmDelete = () => {
    if (!deleteCandidate) return
    setDeleting(true)
    router.delete(`/topics/${deleteCandidate.id}`, {
      preserveScroll: true,
      onSuccess: () => { setSelectedId(null) },
      onFinish: () => { setDeleting(false); setDeleteCandidate(null) },
    })
  }

  return (
    <AppShell>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <PageHeader
            eyebrow="Topics"
            title="Topics"
            actions={<Button onClick={startNew}>+ New topic</Button>}
          />
        </div>
        <div className="pt-9">
          <HelpHint
            heading="What are topics?"
            body={
              <>
                <p>
                  Topics are short labels for the things your agent helps with —
                  like "Rewards", "Payouts", or "Bug reports".
                </p>
                <p className="mt-2">
                  When a person needs human help, the agent picks a topic so the
                  right human knows what's going on.
                </p>
                <p className="mt-2">Empty list = no escalation possible.</p>
              </>
            }
          />
        </div>
      </div>

      {topics.length === 0 && (
        <div className="rounded-card border border-status-warning/40 bg-status-warning/10 px-4 py-3 text-sm text-app-text">
          <strong className="font-semibold">Heads up:</strong>{" "}
          Add at least one topic so your agent can hand off conversations to a human.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <TopicList
          agents={agents}
          selectedAgentId={selectedAgentId}
          onAgentChange={handleAgentChange}
          topics={topics}
          selectedId={selectedId}
          onSelect={handleSelect}
        />

        {selectedId !== null ? (
          <TopicForm
            topic={selected}
            agentId={selectedAgentId}
            tagSuggestions={tagSuggestions}
            isDraft={isDraft}
            onSaved={handleSaved}
            onCancel={handleCancel}
            onDelete={handleDelete}
          />
        ) : (
          <div className="flex items-center justify-center rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
            Select a topic or click + New topic.
          </div>
        )}
      </div>

      <Dialog open={deleteCandidate !== null} onClose={() => !deleting && setDeleteCandidate(null)} size="sm">
        <DialogHeader title="Delete topic?" subtitle="This cannot be undone." />
        <DialogBody>
          <p className="text-sm text-app-text">
            Permanently delete <span className="font-medium">"{deleteCandidate?.name}"</span>?
            Removes the topic from the help menu and unlinks{" "}
            {deleteCandidate?.convoCount30d ?? 0} recent conversation tag{deleteCandidate?.convoCount30d === 1 ? "" : "s"}.
          </p>
        </DialogBody>
        <DialogFooter>
          <button type="button" onClick={() => setDeleteCandidate(null)} disabled={deleting}
            className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5 disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={confirmDelete} disabled={deleting}
            className="rounded-control border border-status-error bg-status-error/90 px-4 py-2 text-sm font-medium text-white transition hover:bg-status-error disabled:opacity-50">
            {deleting ? "Deleting…" : "Delete topic"}
          </button>
        </DialogFooter>
      </Dialog>
    </AppShell>
  )
}
